'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home, Bug, X, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [showReportModal, setShowReportModal] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    console.error('Marketing route error:', error);
  }, [error]);

  const handleSubmitReport = async () => {
    if (!comment.trim()) {
      toast.error('Please describe what you were doing when this error occurred.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comment: `[ERROR PAGE REPORT]\n\nUser description:\n${comment.trim()}\n\nError: ${error?.message || 'Unknown'}\nDigest: ${error?.digest || 'N/A'}`,
          activeFilters: [],
          url: typeof window !== 'undefined' ? window.location.href : 'N/A',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        toast.error('Failed to submit bug report. Please email us at hello@quantwheel.com.');
        return;
      }

      toast.success('Bug report submitted! Thank you for your feedback.');
      setShowReportModal(false);
      setComment('');
    } catch (err) {
      toast.error('Failed to submit bug report. Please email us at hello@quantwheel.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <div className="flex flex-col items-center space-y-4 text-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertTriangle className="h-10 w-10 text-destructive" />
          </div>
          
          <h2 className="text-2xl  tracking-tight">
            Something went wrong
          </h2>
          
          <p className="max-w-md text-muted-foreground">
            We encountered an unexpected error. Please try again.
          </p>

          {process.env.NODE_ENV === 'development' && error?.message && (
            <div className="mt-4 max-w-lg rounded-md border border-destructive/20 bg-destructive/5 p-4 text-left">
              <p className="text-sm font-medium text-destructive">Error details:</p>
              <p className="mt-1 text-sm text-muted-foreground font-mono break-all">
                {error.message}
              </p>
              {error.digest && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Digest: {error.digest}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-4">
            <Button
              variant="default"
              onClick={() => reset()}
              size="sm"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try again
            </Button>
            
            <Link 
              href="/"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
            >
              <Home className="h-4 w-4" />
              Home
            </Link>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReportModal(true)}
            >
              <Bug className="mr-2 h-4 w-4" />
              Report
            </Button>
          </div>
        </div>
      </div>

      {/* Bug Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setShowReportModal(false)}
          />
          <div className="relative z-50 w-full max-w-md rounded-lg border bg-background p-6 shadow-lg mx-4">
            <button 
              onClick={() => setShowReportModal(false)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
            
            <div className="flex items-center gap-2 mb-4">
              <Bug className="h-5 w-5 text-destructive" />
              <h3 className="text-lg">Report this error</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Help us fix this issue by describing what you were doing when the error occurred.
            </p>
            
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="I was trying to... when this error appeared."
              className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              {comment.length}/1000
            </p>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReportModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitReport}
                disabled={isSubmitting || !comment.trim()}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
